import React, { useState, useRef, useEffect } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon, EyeIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../../styles/quill-editor.css';
import { toast } from 'react-hot-toast';

// Custom styles for Quill editor in modal
const quillModalStyles = `
  .email-template-modal .ql-container {
    flex: 1;
    overflow: hidden;
  }
  .email-template-modal .ql-editor {
    min-height: 200px;
    max-height: none;
    overflow-y: auto;
  }
  .email-template-modal .ql-toolbar {
    border-top: 1px solid #e5e7eb;
    border-left: 1px solid #e5e7eb;
    border-right: 1px solid #e5e7eb;
  }
  .email-template-modal .ql-container {
    border-bottom: 1px solid #e5e7eb;
    border-left: 1px solid #e5e7eb;
    border-right: 1px solid #e5e7eb;
  }
`;

const EmailTemplateModal = ({ 
  isOpen, 
  onClose, 
  template = null, 
  onSave, 
  templateTypes = [],
  onPreview 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    action: '',
    subject: '',
    content: '',
    description: '',
    isActive: true,
    isDefault: false,
    variables: []
  });

  const [customVariables, setCustomVariables] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('user');
  const quillRef = useRef(null);

  // Comprehensive variable definitions
  const allAvailableVariables = {
    user: [
      { name: '{{userName}}', description: 'Full name of the user', example: 'John Doe', category: 'User' },
      { name: '{{userEmail}}', description: 'Email address of the user', example: 'john@example.com', category: 'User' },
      { name: '{{userFirstName}}', description: 'First name of the user', example: 'John', category: 'User' },
      { name: '{{userLastName}}', description: 'Last name of the user', example: 'Doe', category: 'User' },
      { name: '{{userPhone}}', description: 'Phone number of the user', example: '+1234567890', category: 'User' }
    ],
    form: [
      { name: '{{formName}}', description: 'Name of the form', example: 'Business Registration Form', category: 'Form' },
      { name: '{{formId}}', description: 'Unique form identifier', example: 'FORM-2024-001', category: 'Form' },
      { name: '{{submissionDate}}', description: 'Date when form was submitted', example: '2024-01-15', category: 'Form' },
      { name: '{{submissionId}}', description: 'Unique submission identifier', example: 'SUB-2024-001', category: 'Form' },
      { name: '{{formStatus}}', description: 'Current status of the form', example: 'In Progress', category: 'Form' }
    ],
    client: [
      { name: '{{clientName}}', description: 'Name of the client', example: 'ABC Company Ltd', category: 'Client' },
      { name: '{{clientEmail}}', description: 'Email address of the client', example: 'contact@abc.com', category: 'Client' },
      { name: '{{clientPhone}}', description: 'Phone number of the client', example: '+1234567890', category: 'Client' },
      { name: '{{clientAddress}}', description: 'Address of the client', example: '123 Business St, City', category: 'Client' }
    ],
    product: [
      { name: '{{productName}}', description: 'Name of the product', example: 'Business License', category: 'Product' },
      { name: '{{productDescription}}', description: 'Description of the product', example: 'Annual business license', category: 'Product' },
      { name: '{{totalFees}}', description: 'Total fees for the product', example: '$1,000.00', category: 'Product' },
      { name: '{{governmentFees}}', description: 'Government fees amount', example: '$500.00', category: 'Product' },
      { name: '{{professionalFees}}', description: 'Professional fees amount', example: '$300.00', category: 'Product' },
      { name: '{{otherFees}}', description: 'Other fees amount', example: '$200.00', category: 'Product' }
    ],
    invoice: [
      { name: '{{invoiceNumber}}', description: 'Invoice number', example: 'INV-2024-001', category: 'Invoice' },
      { name: '{{invoiceDate}}', description: 'Date of invoice', example: '2024-01-15', category: 'Invoice' },
      { name: '{{dueDate}}', description: 'Due date for payment', example: '2024-02-15', category: 'Invoice' },
      { name: '{{totalAmount}}', description: 'Total invoice amount', example: '$1,000.00', category: 'Invoice' },
      { name: '{{paymentLink}}', description: 'Link to payment page', example: 'https://pay.example.com/inv-001', category: 'Invoice' }
    ],
    system: [
      { name: '{{companyName}}', description: 'Your company name', example: 'Data Manager Pro', category: 'System' },
      { name: '{{companyEmail}}', description: 'Your company email', example: 'support@datamanager.com', category: 'System' },
      { name: '{{companyPhone}}', description: 'Your company phone', example: '+1234567890', category: 'System' },
      { name: '{{companyAddress}}', description: 'Your company address', example: '123 Tech St, City', category: 'System' },
      { name: '{{websiteUrl}}', description: 'Your website URL', example: 'https://datamanager.com', category: 'System' },
      { name: '{{loginUrl}}', description: 'Login page URL', example: 'https://datamanager.com/login', category: 'System' },
      { name: '{{supportEmail}}', description: 'Support email address', example: 'support@datamanager.com', category: 'System' }
    ],
    datetime: [
      { name: '{{currentDate}}', description: 'Current date', example: '2024-01-15', category: 'Date/Time' },
      { name: '{{currentTime}}', description: 'Current time', example: '14:30:00', category: 'Date/Time' },
      { name: '{{expiryDate}}', description: 'Expiry date', example: '2024-12-31', category: 'Date/Time' },
      { name: '{{deadlineDate}}', description: 'Deadline date', example: '2024-02-15', category: 'Date/Time' }
    ],
    verification: [
      { name: '{{verificationCode}}', description: 'Verification code', example: '123456', category: 'Verification' },
      { name: '{{verificationLink}}', description: 'Verification link', example: 'https://verify.example.com/code', category: 'Verification' },
      { name: '{{expiryTime}}', description: 'Expiry time for verification', example: '10 minutes', category: 'Verification' }
    ],
    task: [
      { name: '{{taskSequence}}', description: 'Task sequence number', example: '123', category: 'Task' },
      { name: '{{taskName}}', description: 'Task name', example: 'Complete document verification', category: 'Task' },
      { name: '{{taskCustomId}}', description: 'Task custom ID', example: 'TK-2024-001', category: 'Task' },
      { name: '{{taskDescription}}', description: 'Task description', example: 'Verify all submitted documents', category: 'Task' },
      { name: '{{taskStatus}}', description: 'Task status (pending, in_progress, completed, cancelled)', example: 'pending', category: 'Task' },
      { name: '{{taskDeadline}}', description: 'Task deadline date', example: '2024-02-15', category: 'Task' },
      { name: '{{taskCreatedAt}}', description: 'Task creation date', example: '2024-01-15', category: 'Task' },
      { name: '{{taskUpdatedAt}}', description: 'Task last update date', example: '2024-01-16', category: 'Task' },
      { name: '{{taskClient._id}}', description: 'Client ID', example: '507f1f77bcf86cd799439011', category: 'Task' },
      { name: '{{taskClient.sequence}}', description: 'Client sequence number', example: '45', category: 'Task' },
      { name: '{{taskClient.clientName}}', description: 'Client name', example: 'ABC Company Ltd', category: 'Task' },
      { name: '{{taskClient.clientEmail}}', description: 'Client email', example: 'contact@abc.com', category: 'Task' },
      { name: '{{taskClient.clientPhone}}', description: 'Client phone', example: '+1234567890', category: 'Task' },
      { name: '{{taskClient.clientAddress}}', description: 'Client address', example: '123 Business St, City', category: 'Task' },
      { name: '{{taskClient.companyName}}', description: 'Company name', example: 'ABC Corp', category: 'Task' },
      { name: '{{taskClient.companyAddress}}', description: 'Company address', example: '456 Corporate Ave', category: 'Task' },
      { name: '{{taskClient.country}}', description: 'Country', example: 'India', category: 'Task' },
      { name: '{{taskClient.state}}', description: 'State', example: 'Maharashtra', category: 'Task' },
      { name: '{{taskClient.region}}', description: 'Region', example: 'Mumbai', category: 'Task' },
      { name: '{{taskClient.pincode}}', description: 'Pincode', example: '400001', category: 'Task' },
      { name: '{{taskCreatedBy._id}}', description: 'Task creator user ID', example: '507f1f77bcf86cd799439012', category: 'Task' },
      { name: '{{taskCreatedBy.firstName}}', description: 'Task creator first name', example: 'John', category: 'Task' },
      { name: '{{taskCreatedBy.lastName}}', description: 'Task creator last name', example: 'Doe', category: 'Task' },
      { name: '{{taskCreatedBy.email}}', description: 'Task creator email', example: 'john@example.com', category: 'Task' },
      { name: '{{taskCreatedBy.phone}}', description: 'Task creator phone', example: '+1234567890', category: 'Task' },
      { name: '{{#each taskAssignedTo}}', description: 'Start loop for assigned users', example: '{{#each taskAssignedTo}}...{{/each}}', category: 'Task', isLoop: true },
      { name: '{{taskAssignedTo._id}}', description: 'Assigned user ID (within loop)', example: '507f1f77bcf86cd799439013', category: 'Task' },
      { name: '{{taskAssignedTo.firstName}}', description: 'Assigned user first name (within loop)', example: 'Jane', category: 'Task' },
      { name: '{{taskAssignedTo.lastName}}', description: 'Assigned user last name (within loop)', example: 'Smith', category: 'Task' },
      { name: '{{taskAssignedTo.email}}', description: 'Assigned user email (within loop)', example: 'jane@example.com', category: 'Task' },
      { name: '{{taskAssignedTo.phone}}', description: 'Assigned user phone (within loop)', example: '+1234567891', category: 'Task' },
      { name: '{{#each taskAssignedGroups}}', description: 'Start loop for assigned groups', example: '{{#each taskAssignedGroups}}...{{/each}}', category: 'Task', isLoop: true },
      { name: '{{taskAssignedGroups._id}}', description: 'Assigned group ID (within loop)', example: '507f1f77bcf86cd799439014', category: 'Task' },
      { name: '{{taskAssignedGroups.sequence}}', description: 'Assigned group sequence (within loop)', example: '10', category: 'Task' },
      { name: '{{taskAssignedGroups.name}}', description: 'Assigned group name (within loop)', example: 'Legal Team', category: 'Task' },
      { name: '{{taskAssignedGroups.description}}', description: 'Assigned group description (within loop)', example: 'Legal department group', category: 'Task' },
      { name: '{{#each taskVerificationTypes}}', description: 'Start loop for verification types', example: '{{#each taskVerificationTypes}}...{{/each}}', category: 'Task', isLoop: true },
      { name: '{{taskVerificationTypes.value}}', description: 'Verification type value (within loop)', example: 'Email Verification', category: 'Task' },
      { name: '{{taskVerificationTypes._id}}', description: 'Verification type ID (within loop)', example: '507f1f77bcf86cd799439015', category: 'Task' },
      { name: '{{taskVerificationTypes.name}}', description: 'Verification type name (within loop)', example: 'Email Verification', category: 'Task' },
      { name: '{{taskVerificationTypes.description}}', description: 'Verification type description (within loop)', example: 'Verify email address', category: 'Task' },
      { name: '{{#each taskAttachments}}', description: 'Start loop for task attachments', example: '{{#each taskAttachments}}...{{/each}}', category: 'Task', isLoop: true },
      { name: '{{taskAttachments.name}}', description: 'Attachment name (within loop)', example: 'document.pdf', category: 'Task' },
      { name: '{{taskAttachments.url}}', description: 'Attachment URL (within loop)', example: 'https://example.com/files/document.pdf', category: 'Task' },
      { name: '{{taskAttachments.uploadedAt}}', description: 'Attachment upload date (within loop)', example: '2024-01-15', category: 'Task' },
      { name: '{{taskAttachments.uploadedBy._id}}', description: 'Attachment uploader user ID (within loop)', example: '507f1f77bcf86cd799439016', category: 'Task' },
      { name: '{{taskAttachments.uploadedBy.firstName}}', description: 'Attachment uploader first name (within loop)', example: 'Bob', category: 'Task' },
      { name: '{{taskAttachments.uploadedBy.lastName}}', description: 'Attachment uploader last name (within loop)', example: 'Wilson', category: 'Task' },
      { name: '{{taskAttachments.uploadedBy.email}}', description: 'Attachment uploader email (within loop)', example: 'bob@example.com', category: 'Task' },
      { name: '{{#each taskComments}}', description: 'Start loop for task comments', example: '{{#each taskComments}}...{{/each}}', category: 'Task', isLoop: true },
      { name: '{{taskComments.text}}', description: 'Comment text (within loop)', example: 'Please review the documents', category: 'Task' },
      { name: '{{taskComments.createdAt}}', description: 'Comment creation date (within loop)', example: '2024-01-15', category: 'Task' },
      { name: '{{taskComments.user._id}}', description: 'Comment author user ID (within loop)', example: '507f1f77bcf86cd799439017', category: 'Task' },
      { name: '{{taskComments.user.firstName}}', description: 'Comment author first name (within loop)', example: 'Alice', category: 'Task' },
      { name: '{{taskComments.user.lastName}}', description: 'Comment author last name (within loop)', example: 'Brown', category: 'Task' },
      { name: '{{taskComments.user.email}}', description: 'Comment author email (within loop)', example: 'alice@example.com', category: 'Task' },
      { name: '{{taskVerificationData}}', description: 'Task verification data object', example: '{"field1": "value1"}', category: 'Task' },
      { name: '{{taskFormSubmissionData}}', description: 'Task form submission data object', example: '{"field1": "value1"}', category: 'Task' },
      { name: '{{taskOtp}}', description: 'Task OTP data object', example: '{"code": "123456"}', category: 'Task' }
    ],
    iterator: [
      { name: '{{#each products}}', description: 'Start loop for products array', example: '{{#each products}}...{{/each}}', category: 'Iterator', isLoop: true },
      { name: '{{/each}}', description: 'End loop for products array', example: '{{#each products}}...{{/each}}', category: 'Iterator', isLoop: true },
      { name: '{{#each documents}}', description: 'Start loop for documents array', example: '{{#each documents}}...{{/each}}', category: 'Iterator', isLoop: true },
      { name: '{{#each contactPersons}}', description: 'Start loop for contact persons array', example: '{{#each contactPersons}}...{{/each}}', category: 'Iterator', isLoop: true },
      { name: '{{#each fees}}', description: 'Start loop for fees array', example: '{{#each fees}}...{{/each}}', category: 'Iterator', isLoop: true },
      { name: '{{#each taskAssignedTo}}', description: 'Start loop for task assigned users', example: '{{#each taskAssignedTo}}...{{/each}}', category: 'Iterator', isLoop: true },
      { name: '{{#each taskAssignedGroups}}', description: 'Start loop for task assigned groups', example: '{{#each taskAssignedGroups}}...{{/each}}', category: 'Iterator', isLoop: true },
      { name: '{{#each taskVerificationTypes}}', description: 'Start loop for task verification types', example: '{{#each taskVerificationTypes}}...{{/each}}', category: 'Iterator', isLoop: true },
      { name: '{{#each taskAttachments}}', description: 'Start loop for task attachments', example: '{{#each taskAttachments}}...{{/each}}', category: 'Iterator', isLoop: true },
      { name: '{{#each taskComments}}', description: 'Start loop for task comments', example: '{{#each taskComments}}...{{/each}}', category: 'Iterator', isLoop: true },
      { name: '{{#if condition}}', description: 'Start conditional block', example: '{{#if hasDocuments}}...{{/if}}', category: 'Iterator', isConditional: true },
      { name: '{{/if}}', description: 'End conditional block', example: '{{#if hasDocuments}}...{{/if}}', category: 'Iterator', isConditional: true },
      { name: '{{#unless condition}}', description: 'Start unless block (opposite of if)', example: '{{#unless isCompleted}}...{{/unless}}', category: 'Iterator', isConditional: true },
      { name: '{{/unless}}', description: 'End unless block', example: '{{#unless isCompleted}}...{{/unless}}', category: 'Iterator', isConditional: true }
    ],
    loopVariables: [
      { name: '{{@index}}', description: 'Current index in the loop (0-based)', example: '0, 1, 2...', category: 'Loop Variables', isLoopVar: true },
      { name: '{{@first}}', description: 'True if this is the first item in the loop', example: 'true/false', category: 'Loop Variables', isLoopVar: true },
      { name: '{{@last}}', description: 'True if this is the last item in the loop', example: 'true/false', category: 'Loop Variables', isLoopVar: true },
      { name: '{{@key}}', description: 'Key name for object iteration', example: 'propertyName', category: 'Loop Variables', isLoopVar: true },
      { name: '{{@root}}', description: 'Access root context from within a loop', example: '{{@root.clientName}}', category: 'Loop Variables', isLoopVar: true }
    ]
  };

  const categoryLabels = {
    user: '👤 User',
    form: '📋 Form',
    client: '🏢 Client',
    product: '📦 Product',
    invoice: '🧾 Invoice',
    system: '⚙️ System',
    datetime: '📅 Date/Time',
    verification: '🔐 Verification',
    task: '✅ Task',
    iterator: '🔄 Iterators',
    loopVariables: '🔢 Loop Variables'
  };

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        type: template.type || '',
        action: template.action || '',
        subject: template.subject || '',
        content: template.content || '',
        description: template.description || '',
        isActive: template.isActive !== undefined ? template.isActive : true,
        isDefault: template.isDefault || false,
        variables: template.variables || []
      });
      setCustomVariables(template.customVariables || []);
    } else {
      setFormData({
        name: '',
        type: '',
        action: '',
        subject: '',
        content: '',
        description: '',
        isActive: true,
        isDefault: false,
        variables: []
      });
      setCustomVariables([]);
    }
  }, [template]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Function to insert variable at cursor position
  const insertVariable = (variableName) => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection();
      
      if (range) {
        // Handle special loop and conditional variables
        if (variableName.startsWith('{{#each') || variableName.startsWith('{{#if') || variableName.startsWith('{{#unless')) {
          // For loop starters, insert with proper formatting
          const insertText = variableName + '\n\n';
          quill.insertText(range.index, insertText);
          quill.setSelection(range.index + insertText.length);
        } else if (variableName === '{{/each}}' || variableName === '{{/if}}' || variableName === '{{/unless}}') {
          // For loop enders, insert with proper formatting
          const insertText = '\n' + variableName + '\n';   
          quill.insertText(range.index, insertText);
          quill.setSelection(range.index + insertText.length);
        } else if (variableName.startsWith('{{@')) {
          // For loop variables like @index, @first, etc.
          quill.insertText(range.index, variableName);
          quill.setSelection(range.index + variableName.length);
        } else {
          // For regular variables
          quill.insertText(range.index, variableName);
          quill.setSelection(range.index + variableName.length);
        }
        
        // Focus back to the editor
        quill.focus();
        
        toast.success(`Variable ${variableName} inserted`);
      } else {
        // If no selection, insert at the end
        const length = quill.getLength();
        
        if (variableName.startsWith('{{#each') || variableName.startsWith('{{#if') || variableName.startsWith('{{#unless')) {
          const insertText = variableName + '\n\n';
          quill.insertText(length - 1, insertText);
          quill.setSelection(length + insertText.length - 1);
        } else if (variableName === '{{/each}}' || variableName === '{{/if}}' || variableName === '{{/unless}}') {
          const insertText = '\n' + variableName + '\n';
          quill.insertText(length - 1, insertText);
          quill.setSelection(length + insertText.length - 1);
        } else {
          quill.insertText(length - 1, variableName);
          quill.setSelection(length + variableName.length - 1);
        }
        
        quill.focus();
        
        toast.success(`Variable ${variableName} inserted at the end`);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.type || !formData.subject || !formData.content) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Validate action if provided
    if (formData.action) {
      const validActions = ['created', 'updated', 'deleted'];
      if (formData.type === 'task') {
        validActions.push('completed', 'status_changed');
      }
      if (!validActions.includes(formData.action)) {
        toast.error(`Invalid action '${formData.action}' for template type '${formData.type}'`);
        return;
      }
    }

    const submitData = {
      ...formData,
      action: formData.action || null,
      variables: [...formData.variables, ...customVariables]
    };

    onSave(submitData);
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl h-[90vh] flex flex-col email-template-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {template ? 'Edit Email Template' : 'Create Email Template'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {template ? 'Update your email template' : 'Create a new email template'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Left Panel - Variables Tree */}
          <div className="w-1/3 border-r bg-gray-50 flex flex-col min-h-0">
            {/* Variables Header */}
            <div className="p-4 border-b bg-white flex-shrink-0">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Variables</h3>
              <p className="text-xs text-gray-600">Click any variable to insert it into the editor</p>
            </div>

            {/* Category Tabs */}
            <div className="flex border-b bg-white flex-shrink-0">
              <div className="flex-1 overflow-x-auto">
                <div className="flex">
                  {Object.keys(allAvailableVariables).map(category => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                        selectedCategory === category
                          ? 'border-blue-500 text-blue-600 bg-blue-50'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {categoryLabels[category]}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Variables List */}
            <div className="flex-1 overflow-y-auto p-2 min-h-0">
              <div className="space-y-1">
                {allAvailableVariables[selectedCategory]?.map((variable, index) => (
                  <div
                    key={index}
                    onClick={() => insertVariable(variable.name)}
                    className="p-2 rounded cursor-pointer hover:bg-blue-100 transition-colors group"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {variable.name}
                        </div>
                        <div className="text-xs text-gray-600 truncate">
                          {variable.description}
                        </div>
                      </div>
                      <div className="text-xs text-gray-400 group-hover:text-gray-600">
                        Click to insert
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Panel - Editor */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Form Fields */}
            <div className="p-6 border-b bg-white flex-shrink-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter template name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Template Type *
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => {
                      handleInputChange('type', e.target.value);
                      // Reset action when type changes if action is not valid for new type
                      if (e.target.value !== 'task' && ['completed', 'status_changed'].includes(formData.action)) {
                        handleInputChange('action', '');
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select template type</option>
                    {templateTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Action
                  </label>
                  <select
                    value={formData.action}
                    onChange={(e) => handleInputChange('action', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select action (optional)</option>
                    <option value="created">Created</option>
                    <option value="updated">Updated</option>
                    <option value="deleted">Deleted</option>
                    {formData.type === 'task' && (
                      <>
                        <option value="completed">Completed</option>
                        <option value="status_changed">Status Changed</option>
                      </>
                    )}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.type === 'task' 
                      ? 'Task-specific actions: Completed, Status Changed'
                      : 'General actions: Created, Updated, Deleted'}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject *
                  </label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter email subject"
                  />
                </div>
              </div>
            </div>

            {/* Quill Editor */}
            <div className="flex-1 flex flex-col min-h-0">
              <div className="p-4 border-b bg-white flex-shrink-0">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Content *
                </label>
              </div>
              <div className="flex-1 min-h-0 relative" style={{ height: '400px' }}>
                <ReactQuill
                  ref={quillRef}
                  value={formData.content}
                  onChange={(value) => handleInputChange('content', value)}
                  modules={quillModules}
                  placeholder="Write your email content here..."
                  style={{
                    height: '100%'
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50 flex-shrink-0">
          <div className="flex items-center space-x-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Active</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isDefault}
                onChange={(e) => handleInputChange('isDefault', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Set as default</span>
            </label>
          </div>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {template ? 'Update Template' : 'Create Template'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplateModal; 